import { saveToken, clearToken } from "../lib/session";
import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth
  const [error, setError] = useState("");
  const [modules, setModules] = useState(null); // null = keine Einschränkung (Basisrolle)
  const [modulesLoaded, setModulesLoaded] = useState(false);
  // Sichtbarkeit sensibler Finanzdaten (Admin → alles true; Standard sonst alles false)
  const [finance, setFinance] = useState({ balances: false, revenue: false, ek_prices: false, posten_prices: false });

  const loadModules = async () => {
    try {
      const { data } = await api.get("/users/me/modules");
      setModules(Array.isArray(data.modules) ? data.modules : null);
    } catch (_e) {
      setModules(null);
    } finally {
      setModulesLoaded(true);
    }
  };

  const loadFinance = async () => {
    try {
      const { data } = await api.get("/users/me/finance-visibility");
      setFinance(data.flags || { balances: false, revenue: false, ek_prices: false, posten_prices: false });
    } catch (_e) {
      setFinance({ balances: false, revenue: false, ek_prices: false, posten_prices: false });
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await Promise.all([loadModules(), loadFinance()]);
      } catch (_e) {
        setUser(false);
        setModulesLoaded(true);
      }
    })();
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) saveToken(data.token);
      setUser(data.user);
      setModulesLoaded(false);
      await Promise.all([loadModules(), loadFinance()]);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout endpoint failed, clearing local state anyway", e?.message); }
    clearToken();
    setUser(false);
    setModules(null);
    setModulesLoaded(true);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await Promise.all([loadModules(), loadFinance()]);
    } catch (_e) { /* ignore */ }
  };

  // Darf der aktuelle Nutzer das Finanzfeld sehen?
  const canSeeFinance = (field) => !!finance[field];

  return (
    <AuthContext.Provider value={{ user, error, modules, modulesLoaded, finance, canSeeFinance, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
